import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { fmtUSD, fmtNum } from "@/lib/format";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { Wallet, TrendingUp, TrendingDown, PiggyBank, CalendarDays, ArrowUpRight, ArrowDownRight, Sparkles } from "lucide-react";

const AXIS = { fill: "#94a3b8", fontSize: 11 };
const GRID = "rgba(148,163,184,0.18)";
const TIP = { borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", fontSize: 12, background: "hsl(var(--popover))", color: "hsl(var(--popover-foreground))" };

const KPI = ({ label, value, sub, icon: Icon, tone }) => (
  <Card className="rounded-xl p-5 ring-1 ring-border">
    <div className="flex items-center justify-between">
      <span className="text-sm text-muted-foreground">{label}</span>
      <div className="size-8 rounded-lg bg-brand/10 flex items-center justify-center text-brand"><Icon className="size-4" /></div>
    </div>
    <div className="mt-3 font-display text-2xl font-semibold font-mono-num">{value}</div>
    {sub !== undefined && (
      <div className={`mt-1 text-xs flex items-center gap-1 ${tone === "up" ? "text-emerald-600" : tone === "down" ? "text-red-600" : "text-muted-foreground"}`}>
        {tone === "up" && <ArrowUpRight className="size-3" />}{tone === "down" && <ArrowDownRight className="size-3" />}{sub}
      </div>
    )}
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [m, setM] = useState(null);

  useEffect(() => { api.get("/portal/metrics").then(({ data }) => setM(data)).catch(() => {}); }, []);

  if (!m) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28 rounded-xl" />)}</div>
        <Skeleton className="h-80 rounded-xl" />
      </div>
    );
  }

  const chartData = (m.equity_curve || []).map((p) => ({ date: p.date, value: p.equity }));
  const growthUp = m.total_pnl >= 0;
  const monthUp = m.this_month_return >= 0;
  const monthly = (m.monthly_returns || []).slice().reverse(); // newest first for the list
  const maxAbs = Math.max(1, ...(m.monthly_returns || []).map((x) => Math.abs(x.return_pct)));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <h1 className="font-display text-2xl font-semibold tracking-tight">Welcome back, {user?.name?.split(" ")[0]}</h1>
        <p className="text-sm text-muted-foreground mt-1">Here's where your investment stands today <span className="text-amber-600">(simulated)</span>.</p>
      </div>

      {m.welcome_note && (
        <Card data-testid="welcome-note" className="rounded-xl p-5 ring-1 ring-brand/20 bg-brand/[0.06] flex gap-4">
          <div className="size-9 rounded-lg bg-brand/15 flex items-center justify-center text-brand shrink-0"><Sparkles className="size-4" /></div>
          <div>
            <div className="text-sm leading-relaxed text-foreground">{m.welcome_note}</div>
            <div className="mt-2 text-xs text-muted-foreground">— The MentorAI Capital team</div>
          </div>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Current value" value={fmtUSD(m.equity)} sub="Total portfolio value" icon={Wallet} />
        <KPI label="Total growth" value={`${growthUp ? "+" : ""}${fmtUSD(m.total_pnl)}`} sub={`${growthUp ? "+" : ""}${fmtNum(m.roi, 1)}% all-time`} icon={growthUp ? TrendingUp : TrendingDown} tone={growthUp ? "up" : "down"} />
        <KPI label="This month" value={`${monthUp ? "+" : ""}${fmtNum(m.this_month_return, 2)}%`} sub="Return so far this month" icon={CalendarDays} tone={monthUp ? "up" : "down"} />
        <KPI label="Amount invested" value={fmtUSD(m.initial_deposit)} sub="Your initial investment" icon={PiggyBank} />
      </div>

      <Card className="rounded-xl p-5 sm:p-6 ring-1 ring-border">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-medium">Portfolio value over time</h2>
            <p className="text-xs text-muted-foreground">Simulated, last 90 days</p>
          </div>
          <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-700 border-amber-200 text-[11px]">SIMULATED</Badge>
        </div>
        <div className="h-72" data-testid="value-chart">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="pv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0EA5A4" stopOpacity={0.28} />
                  <stop offset="100%" stopColor="#0EA5A4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={GRID} vertical={false} />
              <XAxis dataKey="date" tick={AXIS} minTickGap={40} tickFormatter={(v) => v?.slice(5)} />
              <YAxis tick={AXIS} tickFormatter={(v) => `$${Math.round(v / 1000)}k`} width={56} />
              <Tooltip formatter={(v) => [fmtUSD(v), "Value"]} contentStyle={TIP} />
              <Area type="monotone" dataKey="value" stroke="#0EA5A4" strokeWidth={2.4} fill="url(#pv)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="rounded-xl ring-1 ring-border overflow-hidden">
        <div className="px-5 py-4 border-b">
          <h2 className="font-medium">Monthly returns</h2>
          <p className="text-xs text-muted-foreground">How your portfolio changed each month</p>
        </div>
        <div className="divide-y" data-testid="monthly-returns">
          {monthly.map((row) => {
            const up = row.return_pct >= 0;
            return (
              <div key={row.month_key} className="flex items-center gap-4 px-5 py-3.5">
                <div className="w-24 text-sm font-medium shrink-0">{row.month}</div>
                <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden flex justify-center">
                  <div className="relative w-full flex items-center">
                    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-border" />
                    <div className={`h-2 rounded-full ${up ? "bg-emerald-500 ml-[50%]" : "bg-red-500 mr-[50%] ml-auto"}`}
                         style={{ width: `${(Math.abs(row.return_pct) / maxAbs) * 50}%` }} />
                  </div>
                </div>
                <div className={`w-20 text-right font-mono-num text-sm ${up ? "text-emerald-600" : "text-red-600"}`}>{up ? "+" : ""}{fmtNum(row.return_pct, 2)}%</div>
                <div className="w-28 text-right font-mono-num text-sm text-muted-foreground hidden sm:block">{fmtUSD(row.end_value)}</div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
